import * as THREE from 'three';

/**
 * What a resident holds for its activity: the drinker's clay beaker, the digger's spade, the net-mender's netting needle (the cartographer works on
 * the camp's map table). A prop is fixed to a hand joint in the hand's
 * own frame (the rig's hands hang with the fingers along -y, the thumb toward +z, the palm toward -x on the right hand and +x on the left), so
 * the authored clip moves it with the hand and closes the fingers round it. The Blender scene for a solo clip carries the same props on the same
 * joints (`pair-export.ts`), so what is animated there is what the game shows.
 */
export type ActivityPropKind = 'cup' | 'spade' | 'needle';
export interface ActivityPropPlacement { joint: string; position: [number, number, number]; rotation: [number, number, number] }
export const ACTIVITY_PROP_PLACEMENT: Record<ActivityPropKind, ActivityPropPlacement> = {
  // In the right fist: the beaker's axis across the knuckles, mouth on the thumb side, its side against the palm and the fingers wrapped round it.
  cup: { joint: 'hand.R', position: [-.05, -.08, -.004], rotation: [Math.PI / 2, 0, 0] },
  // In the right fist like a weapon's grip (`weaponGripMount`): the shaft across the knuckles, its blade toward the thumb, the T-handle just past the little finger.
  spade: { joint: 'hand.R', position: [-.0305, -.077, 0], rotation: [0, 0, 0] },
  // Pinched in the right fist, its point out past the thumb.
  needle: { joint: 'hand.R', position: [-.024, -.08, .01], rotation: [0, 0, 0] },
};
export const ACTIVITY_PROPS: Partial<Record<string, ActivityPropKind>> = { drink: 'cup', dig: 'spade', mend: 'needle' };

/** A handleless clay beaker with something dark in it, standing on +y. */
function cup() {
  const clay = new THREE.MeshStandardMaterial({ color: 0x8f6e4e, roughness: .94 });
  const group = new THREE.Group();
  const wall = new THREE.Mesh(new THREE.CylinderGeometry(.04, .034, .095, 14, 1, true), new THREE.MeshStandardMaterial({ color: 0x8f6e4e, roughness: .94, side: THREE.DoubleSide }));
  const base = new THREE.Mesh(new THREE.CylinderGeometry(.034, .033, .008, 14), clay); base.position.y = -.0475;
  const rim = new THREE.Mesh(new THREE.TorusGeometry(.04, .0035, 4, 16), clay); rim.position.y = .0475; rim.rotation.x = Math.PI / 2;
  const drink = new THREE.Mesh(new THREE.CircleGeometry(.038, 14), new THREE.MeshStandardMaterial({ color: 0x3a2716, roughness: .35 }));
  drink.position.y = .03; drink.rotation.x = -Math.PI / 2;
  group.add(wall, base, rim, drink);
  return group;
}

/** A spade along +z from the grip: an ash shaft, a T-handle just behind the hand, an iron blade whose edge is 1.1 m ahead. */
function spade() {
  const wood = new THREE.MeshStandardMaterial({ color: 0x7a5c3e, roughness: .9 }), iron = new THREE.MeshStandardMaterial({ color: 0x55534f, roughness: .6, metalness: .5 });
  const group = new THREE.Group();
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(.0165, .019, .95, 8), wood); shaft.rotation.x = Math.PI / 2; shaft.position.z = .355;
  const grip = new THREE.Mesh(new THREE.CylinderGeometry(.016, .016, .13, 6), wood); grip.rotation.z = Math.PI / 2; grip.position.z = -.12;
  const socket = new THREE.Mesh(new THREE.CylinderGeometry(.021, .03, .12, 8), iron); socket.rotation.x = Math.PI / 2; socket.position.z = .84;
  const blade = new THREE.Mesh(new THREE.BoxGeometry(.2, .008, .27), iron); blade.position.z = .97;
  const edge = new THREE.Mesh(new THREE.CylinderGeometry(.1, .1, .008, 12, 1, false, 0, Math.PI), iron); edge.position.z = 1.08; edge.rotation.y = -Math.PI / 2; edge.scale.set(.35, 1, 1);
  group.add(shaft, grip, socket, blade, edge);
  return group;
}

/** A wooden netting needle with a turn of twine on it, along +z through the fist. */
function needle() {
  const wood = new THREE.MeshStandardMaterial({ color: 0x9b7a52, roughness: .85 }), twine = new THREE.MeshStandardMaterial({ color: 0xa8956c, roughness: 1 });
  const group = new THREE.Group();
  const body = new THREE.Mesh(new THREE.BoxGeometry(.022, .006, .2), wood); body.position.z = .04;
  const point = new THREE.Mesh(new THREE.ConeGeometry(.011, .05, 4), wood); point.rotation.x = Math.PI / 2; point.position.z = .165;
  const wound = new THREE.Mesh(new THREE.BoxGeometry(.03, .016, .1), twine); wound.position.z = .03;
  group.add(body, point, wound);
  return group;
}
const PROP_MODELS: Record<ActivityPropKind, () => THREE.Group> = { cup, spade, needle };

/** Builds a prop and fixes it on its hand joint (from `joints`, the rig's joints by name). */
export function attachActivityProp(kind: ActivityPropKind, joints: Map<string, THREE.Object3D>): THREE.Group {
  const placement = ACTIVITY_PROP_PLACEMENT[kind], prop = PROP_MODELS[kind]();
  prop.name = `civilian:${kind}`;
  prop.position.fromArray(placement.position); prop.rotation.fromArray(placement.rotation);
  prop.traverse(object => { if (object instanceof THREE.Mesh) object.castShadow = object.receiveShadow = true; });
  joints.get(placement.joint)!.add(prop);
  return prop;
}

/** What a walking resident carries in the left hand between stops, and sets down at each stop: a pail of water or a wicker basket. */
export type CarriedPropKind = 'bucket' | 'basket';
/** Its handle's radius, for the fingers' wrap (`poseWeaponGrip`). */
export const CARRIED_HANDLE_RADIUS = .008;

/** A wooden pail hanging from an iron bail: the bail across the fist along z, the pail below it. */
function bucket() {
  const staves = new THREE.MeshStandardMaterial({ color: 0x6e5238, roughness: .9 }), iron = new THREE.MeshStandardMaterial({ color: 0x4d4b47, roughness: .6, metalness: .5 });
  const group = new THREE.Group();
  const pail = new THREE.Mesh(new THREE.CylinderGeometry(.13, .11, .24, 12), staves); pail.position.y = -.24;
  const water = new THREE.Mesh(new THREE.CircleGeometry(.12, 12), new THREE.MeshStandardMaterial({ color: 0x2f3a3c, roughness: .2 })); water.rotation.x = -Math.PI / 2; water.position.y = -.14;
  for (const y of [-.16, -.32]) { const hoop = new THREE.Mesh(new THREE.TorusGeometry(y > -.24 ? .128 : .114, .006, 4, 16), iron); hoop.rotation.x = Math.PI / 2; hoop.position.y = y; group.add(hoop); }
  const bail = new THREE.Mesh(new THREE.TorusGeometry(.13, .005, 4, 16, Math.PI), iron); bail.rotation.y = Math.PI / 2; bail.position.y = -.12; bail.scale.y = .9;
  group.add(pail, water, bail);
  return group;
}
/** A wicker basket of fish or roots on a bent-wood handle. */
function basket() {
  const wicker = new THREE.MeshStandardMaterial({ color: 0x8a6e45, roughness: 1 }), load = new THREE.MeshStandardMaterial({ color: 0x5f6457, roughness: .8 });
  const group = new THREE.Group();
  const body = new THREE.Mesh(new THREE.CylinderGeometry(.17, .13, .18, 10, 1, true), new THREE.MeshStandardMaterial({ color: 0x8a6e45, roughness: 1, side: THREE.DoubleSide })); body.position.y = -.27; body.scale.z = .7;
  const bottom = new THREE.Mesh(new THREE.CircleGeometry(.13, 10), wicker); bottom.rotation.x = Math.PI / 2; bottom.position.y = -.36; bottom.scale.y = .7;
  const heap = new THREE.Mesh(new THREE.SphereGeometry(.15, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), load); heap.position.y = -.2; heap.scale.set(1, .4, .65);
  const handle = new THREE.Mesh(new THREE.TorusGeometry(.16, .008, 4, 14, Math.PI), wicker); handle.rotation.y = Math.PI / 2; handle.position.y = -.2; handle.scale.y = 1.15;
  group.add(body, bottom, heap, handle);
  return group;
}
const CARRIED_MODELS: Record<CarriedPropKind, () => THREE.Group> = { bucket, basket };

/** Builds a carried prop, its handle at the origin and the load hanging below along -y (the way the fingers point when the arm hangs). */
export function carriedProp(kind: CarriedPropKind): THREE.Group {
  const prop = CARRIED_MODELS[kind]();
  prop.name = `civilian:${kind}`;
  prop.traverse(object => { if (object instanceof THREE.Mesh) object.castShadow = object.receiveShadow = true; });
  return prop;
}
